import {
  Box,
  Typography,
  Divider,
  TextField,
  Button,
  Collapse,
  Alert,
  CircularProgress,
  Grid,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import CloseButton from '../../../../components/closebutton/CloseButton';
import { PermissionUpdateModel } from '../../model/permission.model';
import { permissionUpdateSchema } from '../../model/permission.schema';

interface Props {
  loading: boolean;
  message: string | null;
  defaultValues: PermissionUpdateModel | null;
  onClose: () => void;
  onSubmit: (form: PermissionUpdateModel) => void;
}

const PermissionModalUpdate = ({ loading, message, defaultValues, onClose, onSubmit }: Props) => {
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<PermissionUpdateModel>({
    resolver: yupResolver(permissionUpdateSchema),
    defaultValues: defaultValues ?? {},
  });

  return (
    <Box sx={{ width: '100%' }}>
      <Box padding={4}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography fontSize="24px">ແກ້ໄຂສິດທິ</Typography>
          <CloseButton onClick={onClose} />
        </Box>
        <Divider sx={{ backgroundColor: '#1A1A1A20', mt: 1.5, mb: 2.5 }} />

        <Collapse unmountOnExit orientation="vertical" in={!!message}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {message ?? ''}
          </Alert>
        </Collapse>

        <form onSubmit={handleSubmit(onSubmit)}>
          <input type="hidden" {...register('uuid')} />
          <Grid container marginTop="10px" spacing={3}>
            <Grid item md={8} xs={12}>
              <TextField
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                label="ລະຫັດສິດທິ (code)"
                placeholder="mseg:document:create"
                error={!!errors.code}
                helperText={errors.code?.message}
                {...register('code')}
              />
            </Grid>
            <Grid item md={4} xs={12}>
              <TextField
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                label="Module"
                placeholder="mseg"
                error={!!errors.module}
                helperText={errors.module?.message}
                {...register('module')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                label="ຊື່ສິດທິ"
                placeholder="Create Document"
                error={!!errors.name}
                helperText={errors.name?.message}
                {...register('name')}
              />
            </Grid>
          </Grid>

          <Box mt="20px" />
          <TextField
            fullWidth
            size="small"
            multiline
            rows={3}
            InputLabelProps={{ shrink: true }}
            label="ຄໍາອະທິບາຍ"
            placeholder="ປ້ອນຄໍາອະທິບາຍ"
            {...register('description')}
          />

          <Controller
            name="is_active"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                sx={{ mt: 1 }}
                control={
                  <Switch
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                }
                label={field.value ? 'ໃຊ້ງານ' : 'ປິດໃຊ້ງານ'}
              />
            )}
          />

          <Box sx={{ display: 'flex', gap: 2, mt: '30px' }}>
            <Button
              onClick={onClose}
              sx={{ flexGrow: 1, borderRadius: '100px', height: '41px' }}
              variant="outlined"
              size="small"
              fullWidth
            >
              ຍົກເລີກ
            </Button>
            <Button
              type="submit"
              sx={{ flexGrow: 1, borderRadius: '100px', height: '41px' }}
              size="small"
              fullWidth
              variant="contained"
              disabled={loading}
            >
              <Collapse unmountOnExit orientation="horizontal" in={loading}>
                <CircularProgress size="20px" sx={{ color: 'white', marginRight: '10px' }} />
              </Collapse>
              ບັນທຶກ
            </Button>
          </Box>
        </form>
      </Box>
    </Box>
  );
};

export default PermissionModalUpdate;
